/**
 * E07-F02-T18 — "Excluded at search": the companies the discovery search cut, and why.
 *
 * The discovery runner drops a candidate BEFORE it becomes a long-list row when it is
 * on the request's Do-Not-Source lists (company by domain / name, or industry) or its
 * headcount is outside the size window. Wave-1c: that list lived only in the server
 * log, so "0 DNS companies in Review" could not be checked. The run now records it
 * (`job.discoveryExcluded`, research-orchestrator contract) and the request page
 * shows a counter + a collapsed list: company, reason (DNS company / DNS industry /
 * size) and what matched.
 *
 * Read-only: nothing here re-runs a search.
 */
import type { JSX } from 'react';
import {
  describeDiscoveryExclusion,
  discoveryExclusionReasonLabel,
  type DiscoveryExclusion,
  type DiscoveryExclusionReason,
} from '@eer/shared';
import { useResearchStatus } from './researchOrchestratorApi.js';

/** Count per reason (counter line). Pure. */
function discoveryExclusionCounts(
  excluded: readonly DiscoveryExclusion[],
): Record<DiscoveryExclusionReason, number> {
  const out: Record<DiscoveryExclusionReason, number> = {
    dns_company: 0,
    dns_industry: 0,
    size: 0,
    avoid: 0,
    excluded_state: 0,
    dns_global: 0,
  };
  for (const ex of excluded) out[ex.reason] += 1;
  return out;
}

export interface DiscoveryExclusionsListProps {
  excluded: readonly DiscoveryExclusion[];
  testId?: string;
}

export function DiscoveryExclusionsList({
  excluded,
  testId = 'discovery-excluded',
}: DiscoveryExclusionsListProps): JSX.Element {
  const counts = discoveryExclusionCounts(excluded);
  // E07-F02-T22 — "N Excluded state": companies cut for an HQ in an excluded state.
  // E07-F07-T05 — "N EER client (global list)": its own counter, apart from DNS company.
  const parts = (
    ['dns_global', 'dns_company', 'dns_industry', 'avoid', 'excluded_state', 'size'] as const
  )
    .filter((r) => counts[r] > 0)
    .map((r) => `${counts[r]} ${discoveryExclusionReasonLabel(r)}`);
  const summary = (
    <span data-testid="discovery-excluded-count" data-count={excluded.length}>
      <b className="text-slate-800">Excluded at search:</b> {excluded.length}
      {parts.length > 0 ? ` (${parts.join(' · ')})` : ''}
    </span>
  );
  if (excluded.length === 0) {
    return (
      <div
        data-testid={testId}
        data-count={0}
        className="border border-gray-200 rounded-lg mb-4 bg-white text-sm px-4 py-2 text-slate-600"
      >
        {summary} — no company was cut by the Do-Not-Source lists or the size window.
      </div>
    );
  }
  return (
    <details
      data-testid={testId}
      data-count={excluded.length}
      className="border border-gray-200 rounded-lg mb-4 bg-white text-sm"
    >
      <summary className="px-4 py-2 cursor-pointer text-slate-600">{summary}</summary>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Company</th>
              <th className="px-3 py-2 text-left">Website</th>
              <th className="px-3 py-2 text-left">Reason</th>
              <th className="px-3 py-2 text-left">Details</th>
            </tr>
          </thead>
          <tbody>
            {excluded.map((ex, i) => (
              <tr
                key={`${ex.name}-${ex.domain ?? ''}-${i}`}
                data-testid="discovery-excluded-row"
                data-reason={ex.reason}
                className="border-t border-gray-100"
              >
                <td className="px-3 py-2 text-slate-800" data-testid="excluded-name">
                  {ex.name || '—'}
                </td>
                <td className="px-3 py-2 text-slate-500">{ex.domain ?? '—'}</td>
                <td className="px-3 py-2 font-bold text-slate-700" data-testid="excluded-reason">
                  {discoveryExclusionReasonLabel(ex.reason)}
                </td>
                <td className="px-3 py-2 text-slate-600" data-testid="excluded-detail">
                  {describeDiscoveryExclusion(ex)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

/** Review screen: counter + list from the run snapshot (hidden until a run exists). */
export function ReviewDiscoveryExclusions({
  requestId,
}: {
  requestId: number;
}): JSX.Element | null {
  const { data } = useResearchStatus(requestId);
  if (!data) return null;
  return (
    <DiscoveryExclusionsList
      excluded={data.job.discoveryExcluded ?? []}
      testId="review-discovery-excluded"
    />
  );
}
